import { type TransformerConfig } from '../common/types';
import { createDecodeFromDbFn, createEncodeToDbFn, createRenameTransformer } from '../common/utils';
import { createTimestampTransformer } from '../common/sql';
import {
  AssignmentTargetSchema,
  AttemptResponseSchema,
  AttemptSchema,
  AttemptTotalsSchema,
  CriterionMarkSchema,
  DBAssignmentTargetSchema,
  DBAttemptResponseSchema,
  DBAttemptSchema,
  DBAttemptTotalsSchema,
  DBCriterionMarkSchema,
  DBMarkingEventSchema,
  DBSetAssignmentSchema,
  MarkingEventSchema,
  SetAssignmentSchema,
} from './schemas';

export const AttemptCodecConfig: TransformerConfig<typeof AttemptSchema, typeof DBAttemptSchema> = {
  name: 'Attempt',
  clientSchema: AttemptSchema,
  dbSchema: DBAttemptSchema,
  transformers: [
    createTimestampTransformer<typeof AttemptSchema, typeof DBAttemptSchema>('startedAt'),
    createTimestampTransformer<typeof AttemptSchema, typeof DBAttemptSchema>('submittedAt', {
      nullable: true,
    }),
    createTimestampTransformer<typeof AttemptSchema, typeof DBAttemptSchema>('createdAt'),
    createTimestampTransformer<typeof AttemptSchema, typeof DBAttemptSchema>('updatedAt', {
      nullable: true,
    }),
  ],
};

export const SetAssignmentCodecConfig: TransformerConfig<
  typeof SetAssignmentSchema,
  typeof DBSetAssignmentSchema
> = {
  name: 'SetAssignment',
  clientSchema: SetAssignmentSchema,
  dbSchema: DBSetAssignmentSchema,
  transformers: [
    createTimestampTransformer<typeof SetAssignmentSchema, typeof DBSetAssignmentSchema>(
      'opensAt',
      { nullable: true }
    ),
    createTimestampTransformer<typeof SetAssignmentSchema, typeof DBSetAssignmentSchema>(
      'closesAt',
      { nullable: true }
    ),
    createTimestampTransformer<typeof SetAssignmentSchema, typeof DBSetAssignmentSchema>(
      'createdAt'
    ),
    createTimestampTransformer<typeof SetAssignmentSchema, typeof DBSetAssignmentSchema>(
      'updatedAt',
      { nullable: true }
    ),
  ],
};

export const AssignmentTargetCodecConfig: TransformerConfig<
  typeof AssignmentTargetSchema,
  typeof DBAssignmentTargetSchema
> = {
  name: 'AssignmentTarget',
  clientSchema: AssignmentTargetSchema,
  dbSchema: DBAssignmentTargetSchema,
  transformers: [
    createTimestampTransformer<typeof AssignmentTargetSchema, typeof DBAssignmentTargetSchema>(
      'createdAt'
    ),
  ],
};

export const AttemptResponseCodecConfig: TransformerConfig<
  typeof AttemptResponseSchema,
  typeof DBAttemptResponseSchema
> = {
  name: 'AttemptResponse',
  clientSchema: AttemptResponseSchema,
  dbSchema: DBAttemptResponseSchema,
  transformers: [
    createTimestampTransformer<typeof AttemptResponseSchema, typeof DBAttemptResponseSchema>(
      'createdAt'
    ),
    createTimestampTransformer<typeof AttemptResponseSchema, typeof DBAttemptResponseSchema>(
      'updatedAt',
      { nullable: true }
    ),
  ],
};

export const MarkingEventCodecConfig: TransformerConfig<
  typeof MarkingEventSchema,
  typeof DBMarkingEventSchema
> = {
  name: 'MarkingEvent',
  clientSchema: MarkingEventSchema,
  dbSchema: DBMarkingEventSchema,
  transformers: [
    createRenameTransformer<typeof MarkingEventSchema, typeof DBMarkingEventSchema>(
      'markingMethodId',
      'methodId'
    ),
    createTimestampTransformer<typeof MarkingEventSchema, typeof DBMarkingEventSchema>('createdAt'),
  ],
};

export const CriterionMarkCodecConfig: TransformerConfig<
  typeof CriterionMarkSchema,
  typeof DBCriterionMarkSchema
> = {
  name: 'CriterionMark',
  clientSchema: CriterionMarkSchema,
  dbSchema: DBCriterionMarkSchema,
  transformers: [
    createTimestampTransformer<typeof CriterionMarkSchema, typeof DBCriterionMarkSchema>(
      'createdAt'
    ),
  ],
};

export const AttemptTotalsCodecConfig: TransformerConfig<
  typeof AttemptTotalsSchema,
  typeof DBAttemptTotalsSchema
> = {
  name: 'AttemptTotals',
  clientSchema: AttemptTotalsSchema,
  dbSchema: DBAttemptTotalsSchema,
  transformers: [],
};

export const encodeAttemptToDb = createEncodeToDbFn(AttemptCodecConfig);
export const decodeAttemptFromDb = createDecodeFromDbFn(AttemptCodecConfig);
export const encodeSetAssignmentToDb = createEncodeToDbFn(SetAssignmentCodecConfig);
export const decodeSetAssignmentFromDb = createDecodeFromDbFn(SetAssignmentCodecConfig);
export const encodeAssignmentTargetToDb = createEncodeToDbFn(AssignmentTargetCodecConfig);
export const decodeAssignmentTargetFromDb = createDecodeFromDbFn(AssignmentTargetCodecConfig);
export const encodeAttemptResponseToDb = createEncodeToDbFn(AttemptResponseCodecConfig);
export const decodeAttemptResponseFromDb = createDecodeFromDbFn(AttemptResponseCodecConfig);
export const encodeMarkingEventToDb = createEncodeToDbFn(MarkingEventCodecConfig);
export const decodeMarkingEventFromDb = createDecodeFromDbFn(MarkingEventCodecConfig);
export const encodeCriterionMarkToDb = createEncodeToDbFn(CriterionMarkCodecConfig);
export const decodeCriterionMarkFromDb = createDecodeFromDbFn(CriterionMarkCodecConfig);
export const decodeAttemptTotalsFromDb = createDecodeFromDbFn(AttemptTotalsCodecConfig);
